import AddIcon from "@mui/icons-material/AddCircleRounded";
import DeleteIcon from "@mui/icons-material/DeleteRounded";
import { Box, IconButton, Stack } from "@mui/material";
import { FormikTextField } from "@shared";
import { FormikProps } from "formik";

import { AttendanceType } from "@/api";
import {
  AttendanceEntryFormValues,
  AttendancePlanFormBase,
} from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.types";
import { purposeAttendanceType } from "@/common/components/appointments/shared/AppointmentForm.utils";
import { FormikCounselorSelect } from "@/common/components/counselor-select/FormikCounselorSelect";
import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import FormikTimePicker from "@/common/components/fields/formik/formik-time-picker/FormikTimePicker";
import { SystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import { useTranslation } from "@/translations";

import { resolveTimesOnPurposeChange } from "./AttendanceEntryRow.utils";

interface Props<T extends AttendancePlanFormBase> {
  formik: FormikProps<T>;
  dayIndex: number;
  entryIndex: number;
  dayValues: { entries: AttendanceEntryFormValues[]; date?: Date };
  purposeOptions: IOptionItem[];
  purposes: SystemDropdownOptions;
  onAdd?: () => Promise<void>;
  canAdd?: boolean;
  onRemove?: () => Promise<void>;
  updateDayEntries: (
    dayIndex: number,
    updater: (entries: AttendanceEntryFormValues[]) => AttendanceEntryFormValues[]
  ) => Promise<void>;
}

export function AttendanceEntryRow<T extends AttendancePlanFormBase>({
  formik,
  dayIndex,
  entryIndex,
  dayValues,
  purposeOptions,
  purposes,
  onAdd,
  canAdd,
  onRemove,
  updateDayEntries,
}: Props<T>) {
  const { t } = useTranslation();
  // Cast to base type so field path strings resolve correctly regardless of the generic
  const f = formik as unknown as FormikProps<AttendancePlanFormBase>;

  const entry = dayValues.entries[entryIndex];
  const isAbsent = entry?.attendanceType === AttendanceType.Absent;
  const basePath = `days.${dayIndex}.entries.${entryIndex}` as const;

  const handlePurposeChange = async (purposeKey: string | null) => {
    const attendanceType = purposeAttendanceType(purposeKey, purposes);
    const times = resolveTimesOnPurposeChange(attendanceType, entry?.attendanceType ?? null, dayValues.date ?? null);

    await updateDayEntries(dayIndex, (entries) =>
      entries.map((e, ei) =>
        ei !== entryIndex
          ? e
          : {
              ...e,
              purposeKey: purposeKey ?? "",
              attendanceType,
              substituteCounselorId: attendanceType === AttendanceType.Absent ? e.substituteCounselorId : null,
              ...times,
            }
      )
    );
  };

  return (
    <Stack flexDirection={"row"} gap={2} alignItems={"flex-start"}>
      <Box sx={{ width: 140 }}>
        <FormikTimePicker
          formik={f}
          path={`${basePath}.startTime`}
          label={t("calendar.planAttendance.timeFrom")}
          disabled={isAbsent}
          required={!isAbsent}
        />
      </Box>
      <Box sx={{ width: 140 }}>
        <FormikTimePicker
          formik={f}
          path={`${basePath}.endTime`}
          label={t("calendar.planAttendance.timeTo")}
          disabled={isAbsent}
          required={!isAbsent}
        />
      </Box>
      <FormikAutocompleteSelect
        formik={f}
        path={`${basePath}.purposeKey`}
        label={t("calendar.planAttendance.purpose")}
        options={purposeOptions}
        required
        onChange={(_, value) => void handlePurposeChange(value?.id ?? null)}
      />
      {isAbsent && (
        <FormikCounselorSelect
          formik={f}
          path={`${basePath}.substituteCounselorId`}
          label={t("calendar.planAttendance.substitute")}
        />
      )}
      <FormikTextField formik={f} path={`${basePath}.note`} label={t("calendar.planAttendance.note")} />
      {onRemove !== undefined && (
        <IconButton onClick={() => void onRemove()} size="medium" aria-label={t("common.delete")}>
          <DeleteIcon />
        </IconButton>
      )}
      {onAdd !== undefined && (
        <IconButton
          onClick={() => void onAdd()}
          size="medium"
          disabled={!canAdd}
          aria-label={t("calendar.planAttendance.addEntry")}
        >
          <AddIcon />
        </IconButton>
      )}
    </Stack>
  );
}
